import type { FC, ReactNode } from "react";

import { PropertyForm } from "@/pages/postloss/modules/property-valuables/components/PropertyForm";
import { VehicleForm } from "@/pages/postloss/modules/property-valuables/components/VehicleForm";
import { PhysicalAssetForm } from "@/pages/postloss/modules/property-valuables/components/PhysicalAssetForm";
import { LifeInsuranceForm } from "@/pages/postloss/modules/benefits-eligibility/components/LifeInsuranceForm";
import { LtcInsuranceForm } from "@/pages/postloss/modules/benefits-eligibility/components/LtcInsuranceForm";
import { MortgageForm } from "@/pages/postloss/modules/debts-loans/components/MortgageForm";
import { FuneralCostForm } from "@/pages/postloss/modules/expenses/components/FuneralCostForm";
import {
  FinancialAccountForm,
  isFinancialCategory,
} from "@/pages/postloss/modules/financial-accounts/components/FinancialAccountForm";
import { SafeDepositForm } from "@/pages/postloss/modules/property-valuables/components/SafeDepositForm";

import type { EditingState } from "./categoryEditHelpers";

interface CategoryEditFormProps {
  editing: EditingState;
  existingFileName: string | null;
  onFormChange: (form: EditingState["form"]) => void;
  onFileRemove: () => void;
  portalContainer: HTMLElement | null;
  fieldErrors: Partial<Record<string, string>>;
}

export const CategoryEditForm: FC<CategoryEditFormProps> = ({
  editing,
  existingFileName,
  onFormChange,
  onFileRemove,
  portalContainer,
  fieldErrors,
}): ReactNode => {
  switch (editing.category) {
    case "Properties":
      return (
        <PropertyForm
          form={editing.form}
          onChange={(f) => onFormChange(f)}
          existingDeedFileName={existingFileName}
          onDeedRemoved={onFileRemove}
          portalContainer={portalContainer}
          fieldErrors={fieldErrors}
        />
      );
    case "Vehicles":
      return (
        <VehicleForm
          form={editing.form}
          onChange={(f) => onFormChange(f)}
          existingTitleFileName={existingFileName}
          onTitleRemoved={onFileRemove}
          fieldErrors={fieldErrors}
        />
      );
    case "Valuables":
      return (
        <PhysicalAssetForm
          form={editing.form}
          onChange={(f) => onFormChange(f)}
          existingFileName={existingFileName}
          onFileRemoved={onFileRemove}
          fieldErrors={fieldErrors}
        />
      );
    case "Life insurance":
      return (
        <LifeInsuranceForm
          form={editing.form}
          onChange={(f) => onFormChange(f)}
          existingDocumentFileName={existingFileName}
          onDocumentRemoved={onFileRemove}
          fieldErrors={fieldErrors}
        />
      );
    case "Long-term care insurance":
      return (
        <LtcInsuranceForm
          form={editing.form}
          onChange={(f) => onFormChange(f)}
          existingDocumentFileName={existingFileName}
          onDocumentRemoved={onFileRemove}
          fieldErrors={fieldErrors}
        />
      );
    case "Secured debts":
      return (
        <MortgageForm
          form={editing.form}
          onChange={(f) => onFormChange(f)}
          fieldErrors={fieldErrors}
        />
      );
    case "Ongoing Estate Expenses":
      return (
        <FuneralCostForm
          form={editing.form}
          onChange={(f) => onFormChange(f)}
          existingFileName={existingFileName}
          onFileRemoved={onFileRemove}
          fieldErrors={fieldErrors}
        />
      );
    case "Safe or safety deposit box":
      return (
        <SafeDepositForm
          form={editing.form}
          onChange={(f) => onFormChange(f)}
          fieldErrors={fieldErrors}
        />
      );
    default:
      if (isFinancialCategory(editing.category)) {
        return (
          <FinancialAccountForm
            form={editing.form}
            onChange={(f) => onFormChange(f)}
            fieldErrors={fieldErrors}
          />
        );
      }
      return null;
  }
};
